
// Vue
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";

// Api
import api from "@/services/api.service.ts";

// Types
import { ITaskDynamicDataTypes, ISyncEntry, ISyncType, ISyncSystem, IQuerySync, SyncTypes } from "@/store/sync/types";
import { GroupWithPermissions } from "@/store/areas/types";
import {
	UserManagementState,
	UserDetails,
	UserRole,
	UserRolePermission,
	Permission
} from "@/store/user-management/types";

// Third Party
import moment from "moment";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, maxLength, numeric } from "vuelidate/lib/validators";
import { isEqual, debounce } from "lodash";
import { formatDateMixin } from "@/mixins.ts";

// Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vue-select";
import SureViewIcon from "@/components/SureViewIcon.vue";
import SyncSystemSelect from "@/components/setup/contact-business-sync/SyncSystemSelect.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

// Namespaces
const Areas = namespace("areas");
const Sync = namespace("sync");

@Component({
	mixins: [validationMixin],
	validations: {
		currentSiteEntry: {
			groupId: {
				required,
				maxValue: value => value > 0
			},
			syncTypeId: {
				required,
				maxValue: value => value > 0
			},
			dynamicDataTypeId: {
				required: requiredIf(function() {
					return this.currentSiteEntry.syncTypeId == SyncTypes.dynamicDataList;
				})
			},
			identifier: {
				maxLength: maxLength(500)
			},
			syncSystemId: { required }
		},
		hasDataChanged: {
			required: requiredIf(function(tourStep) {
				const result = this.syncEntries.find(x => x.groupSyncId == this.currentSiteEntry.groupSyncId);
				// isEqual can be pretty tricky. Make sure you pass by value, not reference when comparing.
				// So one should be careful using this function.
				return isEqual(result, this.currentSiteEntry);
			})
		}
	},
	components: {
		"area-tree-select": AreaTreeSelect,
		"sureview-icon": SureViewIcon,
		"v-select": vSelect,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"sync-system-select": SyncSystemSelect
	}
})
export default class SiteSyncSetup extends Vue {
	@Areas.Action private fetchAreaDictionary: () => Promise<void>;
	@Areas.Getter private getAreaTitle: (groupId: number) => string;

	@Sync.Action deleteSyncEntry: (syncEntryId: number) => Promise<void>;
	@Sync.Action updateSyncEntry: (syncEntry: ISyncEntry) => Promise<void>;
	@Sync.Action saveNewSyncEntry: (syncEntry: ISyncEntry) => Promise<void>;
	@Sync.Action fetchDynamicDataList: () => Promise<void>;
	@Sync.Action fetchSyncTypes: () => Promise<void>;
	@Sync.Action getSyncEntries: (pageNumber, pageSize) => Promise<void>;
	@Sync.Action verifySyncEntries: (syncEntriesToVerify: ISyncEntry[]) => Promise<void>;
	@Sync.Action querySyncEntry: (payload: IQuerySync) => Promise<void>;
	@Sync.Action("resyncSyncEntry") private dispatchResyncSyncEntry: (payload: ISyncEntry) => Promise<void>;

	@Sync.Mutation setSyncEntryIsBusyState: (satus: boolean) => void;
	@Sync.Mutation setSyncEntryFilteredPageIndex: (index: number) => void;
	@Sync.Mutation setSyncEntryFilter: (filter: string) => void;
	@Sync.Mutation setUserErrorMessage: (errMessage: string) => void;
	@Sync.Mutation setSyncEntryAtEndStatus: (status: boolean) => void;
	@Sync.Getter getSyncEntryDefaultOrFiltered: ISyncEntry[];

	@Sync.State syncEntriesFiltered: ISyncEntry[];
	@Sync.State siteEntryTemplate: ISyncEntry;
	@Sync.State dynamicDataList: ITaskDynamicDataTypes[];
	@Sync.State syncSystem: ISyncSystem[];
	@Sync.State syncTypes: ISyncType[];
	@Sync.State syncEntries: ISyncEntry[];
	@Sync.State requiredSyncEntriesStatusChecked: ISyncEntry[];
	@Sync.State userErrorMessage: string;
	@Sync.State syncEntryPageIndex: number;
	@Sync.State syncEntryPageSize: number;
	@Sync.State syncEntryAtEnd: boolean;
	@Sync.State syncEntryIsBusy: boolean;
	@Sync.State syncEntryFilteredPageIndex: number;

	@Prop({ default: false, type: Boolean }) public visable;

	private currentSiteEntry: ISyncEntry = {
		syncSystemTitle: "",
		groupSyncId: null,
		groupId: null,
		syncSystemId: null,
		identifier: "",
		enabled: false,
		lastUpdated: null,
		syncStarted: null,
		syncTypeId: null,
		dynamicDataTypeId: null
	};

	private selectedSyncSystem: ISyncSystem = {
		extraValue: "",
		groupId: null,
		host: "",
		password: "",
		port: null,
		serverTypeId: null,
		syncSystemId: null,
		title: "",
		username: "",
		syncEntriesCount: null,
		canSyncDynamicDataList: null
	}

	private term: string = "Sync Entry";
	private filter: string = "";
	private SiteEntryModal: boolean = false;
	private isEditing: boolean = false;
	private loadingContent: boolean = false;

	// A list of groupId for all children under the current selected sync system group id.
	private childnodes:number[] = [];

	private fields: any = [
		{ key: "syncSystemTitle", label: "Sync System", sortable: true },
		{
			key: "groupId",
			label: "Area",
			sortable: true,
			formatter: (groupId, key, item) => {
				return groupId > 0 ? this.getAreaTitleById(groupId) : "N/A";
			}
		},
		{ key: "identifier", label: "Identifier", sortable: false, class: "column-vuebootstrap-table" },
		{ key: "enabled", label: "Enabled", sortable: false },
		{ key: "syncStatus", label: "Sync Status", sortable: false },
		{
			key: "lastUpdated",
			label: "Last Updated",
			sortable: false,
			formatter: (date, key, item) => {
				return formatDateMixin.methods.convertUtcDateTimeToLocalDateTimeString(date);
			}
		},
		{
			key: "syncTypeId",
			label: "Sync Type",
			sortable: false,
			formatter: (syncTypeId, key, item) => {
				return this.getSyncTypeTitle(syncTypeId);
			}
		},
		{ key: "actions", label: "Actions", sortable: false }
	];

	public async mounted(): Promise<void> { 
		this.setSyncEntryIsBusyState(true);
		await this.fetchAreaDictionary();
		await this.fetchSyncTypes();
		await this.fetchDynamicDataList();
		await this.getSyncEntries(this.syncEntryPageIndex, this.syncEntryPageSize);
		this.setSyncEntryIsBusyState(false);
		this.setSyncEntryAtEndStatus(false);
		this.setupSyncTimer();
	}

	private getAreaTitleById (groupId: number) {
		return this.getAreaTitle(groupId) ?? "System";
	}

	private get filteredSyncTypes(): ISyncType[] {
		const syncSystem = this.syncSystem.find(x => x.syncSystemId == this.currentSiteEntry.syncSystemId);
		if (syncSystem && syncSystem.canSyncDynamicDataList) {
			return this.syncTypes;
		} else {
			return this.syncTypes.filter(x => x.syncTypeId !== 2);
		}
	}

	/** MODAL Helper: Generate title. */
	private get ModalTitle(): string {
		if (this.isEditing) {
			if (!this.currentSiteEntry.syncSystemTitle) {
				return "Error: Cannot load system' title";
			}
			return `Editing: ${this.currentSiteEntry.syncSystemTitle}`;
		} else {
			return "Add new Sync Entry";
		}
	}

	@Watch("userErrorMessage")
	private updateModalErrorVisability(newErrMsg: string, oldErrMsg: string): void {
		if (!newErrMsg && newErrMsg === "") return;
		if (newErrMsg === oldErrMsg) return;
		if (newErrMsg) {
			setTimeout(() => {
				this.setUserErrorMessage("");
			}, 3000);
		}
	}

	private getSyncTypeTitle(syncTypeId: number): string {
		const syncType = this.syncTypes.find((type: ISyncType) => type.syncTypeId == syncTypeId);
		if (!syncType) return "Device";
		return syncType.title;
	}

	private openModalNew(): void {
		// Clears data that is linked with the modal.
		this.currentSiteEntry = Object.assign({}, this.siteEntryTemplate);

		this.SiteEntryModal = true;
		this.isEditing = false;
	}

	private openRowModalEdit(item: ISyncEntry, index: number, event: any): void {
		// Clears data that is linked with the modal.
		this.currentSiteEntry = Object.assign({}, this.siteEntryTemplate);

		// We want to pass our object by value, not reference.
		this.currentSiteEntry = Object.assign({}, item);

		this.SiteEntryModal = true;
		this.isEditing = true;
	}

	private async rowModalDelete(): Promise<void> {
		await this.deleteSyncEntry(this.currentSiteEntry.groupSyncId);
		this.SiteEntryModal = false;
	}

	private async rowModalSave(): Promise<void> {
		this.currentSiteEntry.lastUpdated = null;

		const requiresStatusCheck = this.requiredSyncEntriesStatusChecked.length == 0;

		this.isEditing
			? await this.updateSyncEntry(this.currentSiteEntry)
			: await this.saveNewSyncEntry(this.currentSiteEntry);

		if (this.userErrorMessage) return;

		this.SiteEntryModal = false;

		// Clears data that is linked with the modal.
		this.currentSiteEntry = Object.assign({}, this.siteEntryTemplate);

		if (requiresStatusCheck) {
			this.setupSyncTimer();
		}
	}

	/** scrollbar at bottom of Y axis listener */
	private async psHandlerReachEnd(event: any) {
		if (!this.visable) return;

		// First check if we got any content in our list.
		if (!this.getSyncEntryDefaultOrFiltered || this.getSyncEntryDefaultOrFiltered.length == 0) return;

		if (this.syncEntryIsBusy) return;
		if (this.syncEntryAtEnd) return;
		if (this.loadingContent) return;
		if (this.syncEntries.length < this.syncEntryPageSize) return;

		// Lets check we are querying or just getting the next load of data.
		if (this.filter !== "") {
			if (this.syncEntriesFiltered.length == 0) return;
			const payload: IQuerySync = {
				index: this.syncEntryFilteredPageIndex + 1,
				pageSize: this.syncEntryPageSize,
				filter: this.filter
			};

			this.loadingContent = true;
			await this.querySyncEntry(payload);
			this.loadingContent = false;
			return;
		}

		this.loadingContent = true;
		await this.getSyncEntries(this.syncEntryPageIndex + 1, this.syncEntryPageSize);
		this.loadingContent = false;
	}

	private setupSyncTimer() {
		if (this.requiredSyncEntriesStatusChecked.length == 0) return;

		setTimeout(async () => {
			await this.verifySyncEntries(this.requiredSyncEntriesStatusChecked);

			if (this.requiredSyncEntriesStatusChecked.length > 0) {
				this.setupSyncTimer();
				return;
			}
		}, 5000);
	}

	private async searchSyncEntries() {
		if (!this.filter || this.filter === "") {
			this.setSyncEntryFilteredPageIndex(1);
			this.setSyncEntryAtEndStatus(false);
		}

		// Slice filter to be only 250 characters
		this.filter = this.filter.slice(0, 249);

		this.setSyncEntryFilter(this.filter);
		const payload: IQuerySync = {
			index: this.syncEntryFilteredPageIndex,
			pageSize: this.syncEntryPageSize,
			filter: this.filter
		};
		await this.querySyncEntry(payload);
	}

	private debouncedSyncEntryQuery = debounce(async () => {
		await this.searchSyncEntries();
	}, 1000);

	private async resyncSyncEntry(syncEntry: ISyncEntry) {
		const requiresStatusCheck = this.requiredSyncEntriesStatusChecked.length == 0;

		await this.dispatchResyncSyncEntry(syncEntry);

		if (requiresStatusCheck) {
			this.setupSyncTimer();
		}
	}

	// Update currently selected sync system for site.
	private async onSyncSystemSelected(syncSystem: ISyncSystem) {
		this.currentSiteEntry.syncSystemId = syncSystem ? syncSystem.syncSystemId : null;
		this.selectedSyncSystem = syncSystem;

		this.childnodes = await api.subgroups(this.selectedSyncSystem.groupId) as number[];
	}
}
